import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CategoryIcon from '@mui/icons-material/Category';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LockIcon from '@mui/icons-material/Lock';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PublicIcon from '@mui/icons-material/Public';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  ClickAwayListener,
  Grid,
  Link,
  MenuItem,
  Paper,
  Popper,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import ApprovalStatusChip from 'components/chip/ApprovalStatusChip';
import TaskStatusChip from 'components/chip/TaskStatusChip';
import {
  EmptyHint,
  PagedWidgetList,
  StatBreakdownTile,
  StatTile,
  Widget,
  WidgetRow,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import { Role } from 'stores/AuthenticationStoreAdapter';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import {
  ApprovalStatus,
  TaskStatus,
  TicketListResult,
  listTicket,
} from 'stores/TicketStoreAdapter';
import {
  TicketScope,
  TicketTypeSummaryResult,
  listTicketType,
} from 'stores/TicketTypeStoreAdapter';
import { formatDateOrDash } from 'utils/DateTimeFormatter';

import TicketCreateDialog from './components/TicketCreateDialog';
import TicketCreateSplitButton from './components/TicketCreateSplitButton';
import { openTicketInNewTab } from './ticketNavigation';

const ACCENT = '#c2185b';

type HomeTab = 'GLOBAL' | 'PRIVATE' | 'APPROVAL';

/**
 * ヒーロー内の「設定」リンクボタン（副次アクション）。
 * 新規作成の主ボタン（白塗り）と明確に差をつけるため、
 * 透明背景＋細い白アウトラインのゴースト表現にして「設定＝控えめな導線」と分かるようにする。
 */
const HeroSettingsButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <Button
    size="small"
    variant="outlined"
    startIcon={icon}
    sx={{
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: 2,
      px: 1.5,
      py: 0.5,
      whiteSpace: 'nowrap',
      boxShadow: 'none',
      bgcolor: 'transparent',
      color: 'rgba(255,255,255,0.9)',
      borderColor: 'rgba(255,255,255,0.4)',
      '& .MuiButton-startIcon': { color: 'rgba(255,255,255,0.9)' },
      '&:hover': {
        boxShadow: 'none',
        bgcolor: 'rgba(255,255,255,0.12)',
        borderColor: 'rgba(255,255,255,0.7)',
      },
    }}
    onClick={onClick}
  >
    {label}
  </Button>
);

/** ウィジェット見出し右上の遷移リンク（一覧/看板/ガント等） */
const NavLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Link
    component="button"
    variant="caption"
    underline="hover"
    onClick={onClick}
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
  >
    {label}
    <ArrowForwardIcon sx={{ fontSize: 14 }} />
  </Link>
);

const TicketHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession } = useAuth();
  const me = userSession.userId;
  const canManageGlobal = useMemo(
    () => userSession.roles.some((r) => r === ('ADMIN' as Role) || r === ('BACK_OFFICE' as Role)),
    [userSession.roles],
  );

  // 選択中タブを user_profile(AUTO) で保存/復元する
  const [ticketDashboard, setTicketDashboard, ticketDashboardRestored] = useDashboardProfile(
    'ticket-home-dashboard',
    { tab: 'GLOBAL' as HomeTab },
  );
  const tab = ticketDashboard.tab;
  const setTab = React.useCallback(
    (next: HomeTab) => setTicketDashboard((prev) => ({ ...prev, tab: next })),
    [setTicketDashboard],
  );
  const [types, setTypes] = useState<TicketTypeSummaryResult[]>([]);
  const [priorities, setPriorities] = useState<MasterTypeResult[]>([]);
  // タブ（スコープ/分類）ごとに選択中の種別。'' = 全て（デフォルト）
  const [selectedTypeId, setSelectedTypeId] = useState('');

  // ヒーローのチケット作成（スプリットボタン）用
  const [createOpen, setCreateOpen] = useState(false);
  const [createTypeId, setCreateTypeId] = useState<string | undefined>(undefined);
  const activeTypes = useMemo(() => types.filter((t) => t.isActive), [types]);
  const openCreate = (typeId: string) => {
    setCreateTypeId(typeId);
    setCreateOpen(true);
  };

  const TAB_ORDER: HomeTab[] = ['GLOBAL', 'PRIVATE', 'APPROVAL'];
  const changeTab = (next: HomeTab) => {
    setSelectedTypeId(''); // タブ切替で種別選択は「全て」に戻す
    setTab(next);
  };

  // 復元した tab が現在の権限で選べない（GLOBAL は canManageGlobal のみ）場合は PRIVATE へフォールバック。
  React.useEffect(() => {
    if (!ticketDashboardRestored) return;
    if (tab === 'GLOBAL' && !canManageGlobal) {
      setTab('PRIVATE');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDashboardRestored, tab, canManageGlobal]);

  // 自分関連のチケット（scope/status/priority はFE集計する）
  const [myTasks, setMyTasks] = useState<TicketListResult[]>([]);
  const [watched, setWatched] = useState<TicketListResult[]>([]);
  const [approvalAssigned, setApprovalAssigned] = useState<TicketListResult[]>([]);
  const [approvalToApprove, setApprovalToApprove] = useState<TicketListResult[]>([]);
  const [loading, setLoading] = useState(true);

  // 親チケットごとの子ステータス内訳グラフ用データ（未着手/着手中の親＋その子）
  const [parentGraph, setParentGraph] = useState<
    { parent: TicketListResult; children: TicketListResult[] }[]
  >([]);
  const [parentGraphLoading, setParentGraphLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // 種別一覧・優先度マスタを取得する。初回ロードと更新ボタンで共用。
  const reloadMasters = React.useCallback(async () => {
    await Promise.all([
      listTicketType({})
        .then(setTypes)
        .catch(() => setTypes([])),
      getMasterType('priority')
        .then(setPriorities)
        .catch(() => setPriorities([])),
    ]);
  }, []);

  useEffect(() => {
    reloadMasters();
  }, [reloadMasters]);

  // 現在タブに対応する種別の選択肢（先頭に「全て」）
  const typeOptions = useMemo(() => {
    const matched = types.filter((t) =>
      tab === 'APPROVAL'
        ? t.scope === 'GLOBAL' && t.ticketCategory === 'APPROVAL'
        : t.scope === (tab as TicketScope) && t.ticketCategory === 'TASK',
    );
    return [
      { value: '', label: '全て' },
      ...matched.map((t) => ({ value: t.typeId, label: t.name })),
    ];
  }, [types, tab]);

  // 選択中種別を遷移クエリに付与する（未選択なら付けない）
  const typeQuery = () => (selectedTypeId ? `&typeIds=${selectedTypeId}` : '');

  // 自分関連のチケット（担当タスク/ウォッチ/申請担当/承認）を取得する。初回ロードと更新ボタンで共用。
  const reloadMyTickets = React.useCallback(async () => {
    setLoading(true);
    try {
      const [tasks, w, apAssigned, apApprove] = await Promise.all([
        listTicket({ assigneeUserId: me, ticketCategory: 'TASK' }),
        listTicket({ watchedByMe: true }),
        listTicket({ assigneeUserId: me, ticketCategory: 'APPROVAL' }),
        listTicket({ approverUserId: me }),
      ]);
      setMyTasks(tasks.results);
      setWatched(w.results);
      setApprovalAssigned(apAssigned.results);
      setApprovalToApprove(apApprove.results);
    } catch {
      /* ベストエフォート（既存挙動を踏襲し握りつぶす） */
    } finally {
      setLoading(false);
    }
  }, [me]);

  useEffect(() => {
    reloadMyTickets();
  }, [reloadMyTickets]);

  // 親チケットごとの子内訳グラフ: 未着手/着手中の親（TASK・onlyParent）とその子を取得。初回ロードと更新ボタンで共用。
  const reloadParentGraph = React.useCallback(async () => {
    setParentGraphLoading(true);
    try {
      const res = await listTicket({
        ticketCategory: 'TASK',
        onlyParent: true,
        statuses: ['NOT_STARTED', 'IN_PROGRESS'],
      });
      const graph = await Promise.all(
        res.results.map((parent) =>
          listTicket({ parentTicketId: parent.ticketId })
            .then((c) => ({ parent, children: c.results }))
            .catch(() => ({ parent, children: [] as TicketListResult[] })),
        ),
      );
      setParentGraph(graph);
    } catch {
      setParentGraph([]);
    } finally {
      setParentGraphLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadParentGraph();
  }, [reloadParentGraph]);

  // ヒーローの更新ボタン: マスタ・自分のチケット・親グラフをまとめて再取得する
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([reloadMasters(), reloadMyTickets(), reloadParentGraph()]);
    } finally {
      setRefreshing(false);
    }
  }, [reloadMasters, reloadMyTickets, reloadParentGraph]);

  const go = (query: string) => navigate(`/ticket/ticket-list${query}`);

  const isOverdue = (t: TicketListResult) =>
    t.scheduledEndDate !== null &&
    new Date(t.scheduledEndDate) < new Date() &&
    t.status !== 'DONE' &&
    t.status !== 'CLOSED';

  // 選択中種別での絞り込み（'' = 全て）
  const matchesType = (t: TicketListResult) => !selectedTypeId || t.typeId === selectedTypeId;

  const countTasks = (scope: TicketScope, filter: (t: TicketListResult) => boolean) =>
    myTasks.filter((t) => t.scope === scope && matchesType(t) && filter(t)).length;

  const scopeQuery = (scope: TicketScope) =>
    `?assigneeUserId=self&scope=${scope}&ticketCategory=TASK${typeQuery()}`;

  const renderRow = (t: TicketListResult) => (
    <WidgetRow key={t.ticketId} accentColor={ACCENT} onClick={() => openTicketInNewTab(t.ticketId)}>
      <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
        {t.managementId}
      </Typography>
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {t.title}
      </Typography>
      {t.ticketCategory === 'TASK' ? (
        <TaskStatusChip status={t.status as TaskStatus} />
      ) : (
        <ApprovalStatusChip status={t.status as ApprovalStatus} />
      )}
      {t.ticketCategory === 'TASK' && (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {formatDateOrDash(t.scheduledEndDate)}
        </Typography>
      )}
    </WidgetRow>
  );

  // 看板/ガントへ現在の条件（scope＋種別）を引き継いで遷移
  const boardQuery = (scope: TicketScope) => `?scope=${scope}${typeQuery()}`;
  const goBoard = (scope: TicketScope) => navigate(`/ticket/board${boardQuery(scope)}`);
  const goGantt = (scope: TicketScope) => navigate(`/ticket/gantt${boardQuery(scope)}`);

  // スコープタブ（グローバル/プライベート）共通のウィジェット群
  const renderScopeTab = (scope: TicketScope) => {
    const q = scopeQuery(scope);
    const scopeWatched = watched.filter((t) => t.scope === scope && matchesType(t));
    const scopeTasks = myTasks.filter((t) => t.scope === scope && matchesType(t));

    // 親グラフ: 現在scope＋選択種別に一致する親のみ
    const graphParents = parentGraph.filter(
      (g) => g.parent.scope === scope && matchesType(g.parent),
    );

    return (
      <Stack spacing={2.5}>
        {/* 1行目: 未着手 / 着手中 / 期日超過 / ウォッチ中（いずれも総件数のみ） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />}
              label="未着手"
              value={countTasks(scope, (t) => t.status === 'NOT_STARTED')}
              suffix="件"
              color="#757575"
              loading={loading}
              onClick={() => go(`${q}&statuses=NOT_STARTED`)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<PlayCircleOutlineIcon sx={{ fontSize: 18 }} />}
              label="着手中"
              value={countTasks(scope, (t) => t.status === 'IN_PROGRESS')}
              suffix="件"
              color="#ed6c02"
              loading={loading}
              onClick={() => go(`${q}&statuses=IN_PROGRESS`)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<EventBusyIcon sx={{ fontSize: 18 }} />}
              label="期日超過"
              value={countTasks(scope, isOverdue)}
              suffix="件"
              color="#d32f2f"
              loading={loading}
              onClick={() => go(`${q}&overdueOnly=true`)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<VisibilityIcon sx={{ fontSize: 18 }} />}
              label="ウォッチ中"
              value={scopeWatched.length}
              suffix="件"
              color="#00838f"
              loading={loading}
              onClick={() => go(`?watchedByMe=true&scope=${scope}${typeQuery()}`)}
            />
          </Grid>
        </Grid>

        {/* 2行目: 自分の担当タスク / ウォッチ中 */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title="自分の担当タスク"
              action={
                <Stack direction="row" spacing={1.5}>
                  <NavLink label="一覧" onClick={() => go(q)} />
                  <NavLink label="看板" onClick={() => goBoard(scope)} />
                  <NavLink label="ガント" onClick={() => goGantt(scope)} />
                </Stack>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={180} />
              ) : scopeTasks.length === 0 ? (
                <EmptyHint text="担当タスクはありません" />
              ) : (
                <PagedWidgetList
                  items={scopeTasks}
                  keyOf={(t) => t.ticketId}
                  renderItem={renderRow}
                />
              )}
            </Widget>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title="ウォッチ中"
              action={
                <Stack direction="row" spacing={1.5}>
                  <NavLink
                    label="一覧"
                    onClick={() => go(`?watchedByMe=true&scope=${scope}${typeQuery()}`)}
                  />
                  <NavLink label="看板" onClick={() => goBoard(scope)} />
                  <NavLink label="ガント" onClick={() => goGantt(scope)} />
                </Stack>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={180} />
              ) : scopeWatched.length === 0 ? (
                <EmptyHint text="ウォッチ中のチケットはありません" />
              ) : (
                <PagedWidgetList
                  items={scopeWatched}
                  keyOf={(t) => t.ticketId}
                  renderItem={renderRow}
                />
              )}
            </Widget>
          </Grid>
        </Grid>

        {/* 3行目: 親チケットごとの子ステータス内訳（未着手/着手中の親） */}
        <Widget title="タスクの進捗（未着手・着手中の親チケット）">
          {parentGraphLoading ? (
            <Skeleton variant="rounded" height={200} />
          ) : graphParents.length === 0 ? (
            <EmptyHint text="未着手・着手中の親チケットはありません" />
          ) : (
            <Grid container spacing={2}>
              {graphParents.map((g) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={g.parent.ticketId}>
                  <ParentProgressDonut
                    parent={g.parent}
                    childTickets={g.children}
                    onClick={() => navigate(`/ticket/gantt?parentTicketId=${g.parent.ticketId}`)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Widget>
      </Stack>
    );
  };

  // 申請タブ
  const renderApprovalTab = () => {
    const tq = typeQuery();
    const countAssigned = (status: ApprovalStatus) =>
      approvalAssigned.filter((t) => matchesType(t) && t.status === status).length;
    const countApprove = (status: ApprovalStatus) =>
      approvalToApprove.filter((t) => matchesType(t) && t.status === status).length;
    const approvalWatched = watched.filter(
      (t) => t.ticketCategory === 'APPROVAL' && matchesType(t),
    );

    return (
      <Grid container spacing={2}>
        {/* 担当チケット（自分が作成/担当） */}
        <Grid size={{ xs: 12, md: 4 }}>
          <StatBreakdownTile
            icon={<ListAltIcon sx={{ fontSize: 16 }} />}
            label="担当チケット"
            total={
              countAssigned('ADJUSTING') +
              countAssigned('SUBMITTED') +
              countAssigned('IN_PROGRESS') +
              countAssigned('REJECTED')
            }
            color="#1976d2"
            loading={loading}
            onClick={() => go(`?assigneeUserId=self&ticketCategory=APPROVAL${tq}`)}
            breakdown={[
              {
                label: ApprovalStatus.ADJUSTING,
                value: countAssigned('ADJUSTING'),
                onClick: () =>
                  go(`?assigneeUserId=self&ticketCategory=APPROVAL&statuses=ADJUSTING${tq}`),
              },
              {
                label: ApprovalStatus.SUBMITTED,
                value: countAssigned('SUBMITTED'),
                onClick: () =>
                  go(`?assigneeUserId=self&ticketCategory=APPROVAL&statuses=SUBMITTED${tq}`),
              },
              {
                label: ApprovalStatus.IN_PROGRESS,
                value: countAssigned('IN_PROGRESS'),
                onClick: () =>
                  go(`?assigneeUserId=self&ticketCategory=APPROVAL&statuses=IN_PROGRESS${tq}`),
              },
              {
                label: ApprovalStatus.REJECTED,
                value: countAssigned('REJECTED'),
                onClick: () =>
                  go(`?assigneeUserId=self&ticketCategory=APPROVAL&statuses=REJECTED${tq}`),
              },
            ]}
          />
        </Grid>

        {/* 承認チケット（自分が承認者） */}
        <Grid size={{ xs: 12, md: 4 }}>
          <StatBreakdownTile
            icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
            label="承認チケット"
            total={
              countApprove('SUBMITTED') + countApprove('IN_PROGRESS') + countApprove('REJECTED')
            }
            color="#ed6c02"
            loading={loading}
            onClick={() => go(`?approverUserId=self${tq}`)}
            breakdown={[
              {
                label: ApprovalStatus.SUBMITTED,
                value: countApprove('SUBMITTED'),
                onClick: () => go(`?approverUserId=self&statuses=SUBMITTED${tq}`),
              },
              {
                label: ApprovalStatus.IN_PROGRESS,
                value: countApprove('IN_PROGRESS'),
                onClick: () => go(`?approverUserId=self&statuses=IN_PROGRESS${tq}`),
              },
              {
                label: ApprovalStatus.REJECTED,
                value: countApprove('REJECTED'),
                onClick: () => go(`?approverUserId=self&statuses=REJECTED${tq}`),
              },
            ]}
          />
        </Grid>

        {/* ウォッチ中（承認） */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ alignSelf: 'flex-start' }}>
          <StatTile
            icon={<VisibilityIcon sx={{ fontSize: 18 }} />}
            label="ウォッチ中"
            value={approvalWatched.length}
            suffix="件"
            color="#00838f"
            loading={loading}
            onClick={() => go(`?watchedByMe=true&ticketCategory=APPROVAL${tq}`)}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        <DomainHero
          eyebrow="TICKET"
          title="Ticket Management"
          tagline="Manage team tasks and approval workflows, plus your own private to-dos. Track parent-child tickets across list, board, and Gantt views — with comments, watchers, and due-date alerts."
          palette={HERO_PALETTES.ticket}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          actions={
            <Stack direction="column" spacing={1} alignItems="stretch">
              {canManageGlobal && (
                <HeroSettingsButton
                  icon={<PublicIcon sx={{ fontSize: 16 }} />}
                  label="グローバルチケット設定"
                  onClick={() => navigate('/ticket/type/global')}
                />
              )}
              <HeroSettingsButton
                icon={<LockIcon sx={{ fontSize: 16 }} />}
                label="プライベートチケット設定"
                onClick={() => navigate('/ticket/type/private')}
              />
              <TicketCreateSplitButton hero activeTypes={activeTypes} onCreate={openCreate} />
            </Stack>
          }
        />

        {/* タブ切替（グローバル / プライベート / 申請）＋ 右端に種別プルダウン */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box
            sx={{
              width: { xs: '100%', sm: 420 },
              flexShrink: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              p: 0.75,
              bgcolor: 'background.paper',
            }}
          >
            <ToggleButtonGroup
              value={tab}
              exclusive
              fullWidth
              size="small"
              onChange={(_e, v: HomeTab | null) => v && changeTab(v)}
              sx={{
                width: '100%',
                gap: 0.75,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  py: 1,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    bgcolor: 'action.selected',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'action.selected' },
                  },
                },
              }}
            >
              <ToggleButton value="GLOBAL">
                <PublicIcon sx={{ fontSize: 18, mr: 0.75 }} />
                グローバル
              </ToggleButton>
              <ToggleButton value="PRIVATE">
                <LockIcon sx={{ fontSize: 18, mr: 0.75 }} />
                プライベート
              </ToggleButton>
              <ToggleButton value="APPROVAL">
                <HourglassEmptyIcon sx={{ fontSize: 18, mr: 0.75 }} />
                申請
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* 種別プルダウン（全て + 現在タブに対応する種別・デフォルト全て）。
              ウィジェット画面用のピル型セレクト（枠は1つ・トグルと調和）。 */}
          <WidgetSelect
            label="種別"
            value={selectedTypeId}
            options={typeOptions}
            onChange={setSelectedTypeId}
            sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}
          />
        </Stack>

        {/* ビュー本体（管理者ドメインと同じ translateX スライドトラックで切り替え）。
            隣グループのウィジェット枠線が右端に覗かないよう overflow:hidden で厳密にクリップし、
            トラックは calc で正確な1/3ずつスライドさせる（小数丸めのズレを防ぐ）。
            ホバー時の浮き（translateY）が上端で切れないよう縦は余白で吸収する。 */}
        <Box sx={{ overflow: 'hidden', py: 0.5, mt: -0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '300%',
              transition: 'transform 0.3s ease',
              transform: `translateX(calc(${TAB_ORDER.indexOf(tab)} * -100% / 3))`,
            }}
          >
            <Box sx={{ width: 'calc(100% / 3)', flexShrink: 0 }}>{renderScopeTab('GLOBAL')}</Box>
            <Box sx={{ width: 'calc(100% / 3)', flexShrink: 0 }}>{renderScopeTab('PRIVATE')}</Box>
            <Box sx={{ width: 'calc(100% / 3)', flexShrink: 0 }}>{renderApprovalTab()}</Box>
          </Box>
        </Box>
      </Stack>

      {/* ヒーローのチケット作成（スプリットボタン）から開く作成ダイアログ */}
      <TicketCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          reloadMyTickets();
          reloadParentGraph();
        }}
        activeTypes={activeTypes}
        priorities={priorities}
        initialTypeId={createTypeId}
      />
    </Box>
  );
};

/**
 * ウィジェット画面用のピル型セレクト（枠は1つ）。
 * アイコン＋ラベル＋選択値＋シェブロンのクリック領域を、切替トグルと同じピル枠で表現する。
 */
const WidgetSelect: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  sx?: SxProps<Theme>;
}> = ({ label, value, options, onChange, sx }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  return (
    <Box ref={anchorRef} sx={sx}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 44,
          border: 1,
          borderColor: open ? 'primary.main' : 'divider',
          borderRadius: 3,
          px: 1.5,
          cursor: 'pointer',
          bgcolor: 'background.paper',
          transition: 'border-color 0.15s',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <CategoryIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}
        >
          {selected?.label ?? '全て'}
        </Typography>
        <ArrowDropDownIcon
          sx={{
            color: 'action.active',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={0}
            sx={{
              mt: 0.5,
              maxHeight: 280,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            {options.map((o) => (
              <MenuItem
                key={o.value}
                selected={o.value === value}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                sx={{ py: 1 }}
              >
                <Typography variant="body2" fontWeight={o.value === value ? 700 : 400}>
                  {o.label}
                </Typography>
              </MenuItem>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

/** 子タスクのステータス色（未着手/着手中/完了/クローズ） */
const CHILD_STATUS_META: { status: TaskStatus; color: string }[] = [
  { status: 'NOT_STARTED', color: '#9e9e9e' },
  { status: 'IN_PROGRESS', color: '#ed6c02' },
  { status: 'DONE', color: '#2e7d32' },
  { status: 'CLOSED', color: '#455a64' },
];

/**
 * 親チケット1件分の進捗ドーナツ。
 * チーム内レビューの結果、積み上げバーより「完了率」が一目でわかるドーナツ（中央に完了%）を採用。
 * - 子タスクのステータス構成をドーナツで表示（完了=DONE+CLOSED を進捗とみなす）
 * - 中央に完了率(%)
 * - ホバーで「ステータス別件数 / 超過タスク / 前倒しタスク」を表示
 * - クリックで親を条件にしたガントへ遷移
 */
const ParentProgressDonut: React.FC<{
  parent: TicketListResult;
  childTickets: TicketListResult[];
  onClick: () => void;
}> = ({ parent, childTickets, onClick }) => {
  const total = childTickets.length;
  const countByStatus = (s: TaskStatus) => childTickets.filter((c) => c.status === s).length;

  // 超過: 未着手/着手中で 現在日 > 終了予定日
  const overdue = childTickets.filter(
    (c) =>
      (c.status === 'NOT_STARTED' || c.status === 'IN_PROGRESS') &&
      c.scheduledEndDate !== null &&
      new Date(c.scheduledEndDate) < new Date(),
  ).length;
  // 前倒し: 着手中で 開始日 < 開始予定日（開始日・開始予定日が一覧に含まれる場合のみ判定）
  const ahead = childTickets.filter(
    (c) =>
      c.status === 'IN_PROGRESS' &&
      c.actualStartDate &&
      c.scheduledStartDate &&
      new Date(c.actualStartDate) < new Date(c.scheduledStartDate),
  ).length;

  // 完了率（完了+クローズ / 全子）
  const done = countByStatus('DONE') + countByStatus('CLOSED');
  const rate = total > 0 ? Math.round((done / total) * 100) : 0;

  // ドーナツ描画（SVG・stroke-dasharray でステータス比率のアーク）
  const SIZE = 96;
  const STROKE = 12;
  const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  let offset = 0;
  const arcs =
    total === 0
      ? []
      : CHILD_STATUS_META.filter((m) => countByStatus(m.status) > 0).map((m) => {
          const frac = countByStatus(m.status) / total;
          const seg = {
            color: m.color,
            dash: frac * C,
            gap: C - frac * C,
            rot: (offset / total) * 360 - 90,
          };
          offset += countByStatus(m.status);
          return seg;
        });

  const tooltip = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
        {parent.title}（{parent.managementId}）
      </Typography>
      {CHILD_STATUS_META.map((m) => (
        <Stack key={m.status} direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: m.color }} />
            <Typography variant="caption">{TaskStatus[m.status]}</Typography>
          </Stack>
          <Typography variant="caption" fontWeight={700}>
            {countByStatus(m.status)}
          </Typography>
        </Stack>
      ))}
      <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="error.light">
          超過タスク
        </Typography>
        <Typography variant="caption" fontWeight={700} color="error.light">
          {overdue}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" color="success.light">
          前倒しタスク
        </Typography>
        <Typography variant="caption" fontWeight={700} color="success.light">
          {ahead}
        </Typography>
      </Stack>
    </Box>
  );

  const hasChildren = total > 0;

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        onClick={onClick}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          cursor: 'pointer',
          transition: 'border-color 0.15s, transform 0.15s',
          textAlign: 'center',
          '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
        }}
      >
        {/* 親チケットのタイトル（先頭） */}
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          sx={{ display: 'block', mb: 1 }}
          title={parent.title}
        >
          {parent.title}
        </Typography>

        <Box sx={{ position: 'relative', width: SIZE, height: SIZE, mx: 'auto' }}>
          <Box component="svg" width={SIZE} height={SIZE} sx={{ display: 'block' }}>
            {/* トラック（子なしの場合は破線の空リングで枠を維持） */}
            <Box
              component="circle"
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              strokeWidth={STROKE}
              strokeDasharray={hasChildren ? undefined : '4 5'}
              sx={{ stroke: hasChildren ? 'action.hover' : 'divider' }}
            />
            {/* ステータスアーク */}
            {arcs.map((a, i) => (
              <circle
                key={i}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={R}
                fill="none"
                stroke={a.color}
                strokeWidth={STROKE}
                strokeDasharray={`${a.dash} ${a.gap}`}
                transform={`rotate(${a.rot} ${SIZE / 2} ${SIZE / 2})`}
              />
            ))}
          </Box>
          {/* 中央: 子ありは完了率 / 子なしは「子なし」 */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {hasChildren ? (
              <>
                <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1 }}>
                  {rate}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  完了
                </Typography>
              </>
            ) : (
              <>
                <RemoveIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">
                  子なし
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            {hasChildren ? `子 ${total} 件` : '子タスクなし'}
          </Typography>
          {overdue > 0 && (
            <Typography variant="caption" color="error.main" fontWeight={700}>
              超過 {overdue}
            </Typography>
          )}
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default TicketHome;
